// =============================================================================
// Entra ID sign-in for the browser.
//
// This is the one part of the mobile app that cannot be shared. The mobile
// client drives the OAuth code flow by hand through expo-auth-session and
// stores tokens in SecureStore; a browser SPA uses MSAL, which owns the
// redirect handling, token cache and silent renewal itself. What matters is
// that both end up holding an access token for the SAME API scope, because
// the Functions backend (verifyEntraBearerToken) validates the audience.
//
// Tokens are never written to localStorage: MSAL is configured with
// sessionStorage so a token cannot outlive the browser session or leak into
// another tab's account, matching the reasoning behind the mobile app's
// in-memory tenant selection.
// =============================================================================
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { setSelectedTenantId } from '@mobile-services/tenantSelection';

const CLIENT_ID = import.meta.env.VITE_AAD_CLIENT_ID || '';
const TENANT_ID = import.meta.env.VITE_AAD_TENANT_ID || '';
const API_SCOPE = import.meta.env.VITE_AAD_API_SCOPE || '';
const MULTITENANT = String(import.meta.env.VITE_AAD_MULTITENANT || '').toLowerCase() === 'true';

// Mirrors getDiscovery() in the mobile auth service: a multi-tenant app
// registration must be reached through the shared "organizations" authority,
// because the signing-in user's tenant isn't known until after sign-in.
const authority = `https://login.microsoftonline.com/${MULTITENANT ? 'organizations' : TENANT_ID || 'common'}`;

export function isEntraConfigured() {
  return Boolean(CLIENT_ID && (MULTITENANT || TENANT_ID));
}

// Built lazily rather than at module scope. MSAL validates its config in the
// constructor, so a bad value (an empty client id, a malformed authority)
// threw while the module was still evaluating - which aborts the whole bundle
// before React mounts and shows the user a blank page with nothing to go on.
// Deferring it means the same failure arrives as a message on screen.
let msalInstance = null;
function getMsal() {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication({
      auth: {
        clientId: CLIENT_ID,
        authority,
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
        navigateToLoginRequestUrl: false,
      },
      cache: { cacheLocation: 'sessionStorage', storeAuthStateInCookie: false },
    });
  }
  return msalInstance;
}

// The API scope is what makes the token usable against the Functions app. If
// it hasn't been configured we fall back to an id token only, which will be
// rejected by the API - surfaced clearly rather than failing per-request.
const apiScopes = API_SCOPE ? [API_SCOPE] : ['openid', 'profile'];

// MSAL keeps its token cache and the state of an in-flight redirect in
// sessionStorage, so a browser that refuses site data cannot sign in at all -
// initialize() throws and the redirect never starts. Safari on iOS refuses it
// whenever "Block All Cookies" is on, which is a setting that can be turned on
// long before anyone tries to log in, and turns up as a sign-in button that
// simply does nothing. Probing it first turns that into a sentence naming the
// setting, rather than an MSAL internal error about a cache it could not read.
export function assertBrowserStorage() {
  try {
    const probe = '__ydyai_storage_probe__';
    window.sessionStorage.setItem(probe, '1');
    window.sessionStorage.removeItem(probe);
  } catch {
    throw new Error(
      'This browser is blocking site data, which Microsoft sign-in needs. On iPhone: Settings > Apps > Safari > Advanced > turn off "Block All Cookies", then try again.'
    );
  }
}

let initPromise = null;
export function initAuth() {
  if (!initPromise) {
    assertBrowserStorage();
    initPromise = getMsal()
      .initialize()
      .then(() => getMsal().handleRedirectPromise())
      .then((result) => {
        const account = result?.account || getMsal().getAllAccounts()[0];
        if (account) getMsal().setActiveAccount(account);
        return account || null;
      });
  }
  return initPromise;
}

export function getAccount() {
  const instance = getMsal();
  return instance.getActiveAccount() || instance.getAllAccounts()[0] || null;
}

export function isSignedIn() {
  return Boolean(getAccount());
}

// True when the app is running inside someone else's page - which, for us,
// means the Procore embedded experience. The cross-origin read of window.top
// throws in exactly the case we care about, so a throw is a yes.
//
// This single fact decides how sign-in runs. Entra refuses to be framed
// (login.microsoftonline.com sends X-Frame-Options: DENY of its own), so a
// redirect flow inside a Procore side panel navigates the frame to a page the
// browser then blocks: the panel goes blank and nothing says why. A popup is
// a top-level window, so Entra renders normally, and MSAL writes the result
// back into this frame's cache.
function isFramed() {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export async function signIn() {
  await initAuth();
  const request = { scopes: apiScopes, prompt: 'select_account' };
  if (isFramed()) {
    const result = await getMsal().loginPopup(request);
    if (result?.account) getMsal().setActiveAccount(result.account);
    return;
  }
  await getMsal().loginRedirect(request);
}

export async function signOut() {
  setSelectedTenantId('');
  await getMsal().logoutRedirect({ account: getAccount() });
}

// Returns a bearer token for the API, renewing silently when possible. A
// silent failure means the session genuinely needs the user again (consent
// revoked, password change, MFA), so it escalates to interaction rather than
// leaving the app in a half-signed-in state issuing 401s - a popup when
// framed, for the same reason signIn() uses one.
export async function getValidAccessToken() {
  await initAuth();
  const account = getAccount();
  if (!account) return '';
  try {
    const result = await getMsal().acquireTokenSilent({ scopes: apiScopes, account });
    return result.accessToken || '';
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      if (isFramed()) {
        const result = await getMsal().acquireTokenPopup({ scopes: apiScopes, account });
        return result?.accessToken || '';
      }
      await getMsal().acquireTokenRedirect({ scopes: apiScopes, account });
      return '';
    }
    throw error;
  }
}

export function getStoredAccount() {
  const account = getAccount();
  if (!account) return null;
  return {
    name: account.name || '',
    email: account.username || '',
    oid: account.idTokenClaims?.oid || '',
    tenantId: account.tenantId || '',
  };
}
